import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { FilterStore } from "stores";

import { TCatalogEntities } from "types";
import Pipeline from "../Pipeline";

import { sortOptionsByFavourites } from "../helper";
import List from "../../List";
import Filter from "../../Filter";

import ModellReducer from "./ModellReducer";
import ModellIndicator from "./ModellIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter: any;
  configuration?: any;
}

interface IModellFilter extends IFilter {
  disabledWithoutMarke?: boolean;
}

export const SideFilter: React.FC<IFilter> = ({
  entities = {},
  configuration = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.modell) return null;

  const items = Object.values(entities.modell);
  const itemsCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === itemsCount ? [] : value;
    dispatchFilter({ type: "UPDATE_FILTER", payload: { modell: newValue } });
  };

  const favourites = configuration?.filter?.favourites?.modell || [];

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: name,
    }))
    .sort(sortOptionsByFavourites(favourites));

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List {...{ value, options, setValueHandler, all, sort: false }} />
    </Box>
  );
  return (
    <Filter
      component={Component}
      title={
        <FormattedMessage id="filter.modell.label" defaultMessage="Standort" />
      }
    />
  );
};

export const SimpleFilter: React.FC<IModellFilter> = ({
  entities = {},
  configuration = {},
  value = [],
  dispatchFilter = () => null,
  disabledWithoutMarke = true,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const {
    state: { filter },
  } = React.useContext(FilterStore);
  if (!entities.modell) return null;

  const setValueHandler = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({ type: "UPDATE_FILTER", payload: { modell: newValue } });
  };

  const favourites = configuration?.filter?.favourites?.modell || [];
  const items = Object.values(entities.modell);

  const options = items
    .filter((modell) => {
      if (filter.marke && filter.marke.length > 0) {
        return filter.marke.includes(modell.marke);
      }
      return true;
    })
    .map(({ id, name }) => ({
      value: id,
      title: name,
    }))
    .sort(sortOptionsByFavourites(favourites));

  return (
    <FormControl
      size="small"
      disabled={
        disabledWithoutMarke && filter.marke && filter.marke.length === 0
      }
    >
      <InputLabel id="filter-modell-simple-label">
        <FormattedMessage id="filter.modell.label" defaultMessage="Modell" />
      </InputLabel>
      <Select
        labelId="filter-modell-simple-label"
        id="filter-modell-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({ id: "filter.modell.label", defaultMessage: "Modell" })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>
          {t({ id: "common.alle", defaultMessage: "Alle" })}
        </MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"modell-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const variants = new Map([
  ["side", SideFilter],
  ["simple", SimpleFilter],
]);

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variants.get(variant) || SimpleFilter;
  return (
    <Pipe
      {...{
        type: "modell",
        defaultValue: [],
        Component: Filter,
        Reducer: ModellReducer,
        Indicator: ModellIndicator,
      }}
    />
  );
};

export default Container;
